import React from 'react';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import {
  Box,
  FormControl,
  FormHelperText,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_SALON_CONTENT_STYLE,
  DEFAULT_SALON_TITLE_STYLE,
} from '../../../theme/salonPublicLayout';
import {
  DEFAULT_SALON_PUBLIC_THEME,
  SALON_FONT_CATALOG,
  buildSalonPublicTheme,
} from '../../../theme/salonPublicTheme';
import SalonEffectiveColorControl from './SalonEffectiveColorControl';


const SIZE_LABELS = Object.freeze({
  xs: '12',
  sm: '14',
  md: '16',
  lg: '24',
  xl: '32',
});

const firstError = (error) => (Array.isArray(error) ? error[0] : error || '');

const SalonTextStyleControls = ({
  sectionKey,
  role,
  value,
  theme = DEFAULT_SALON_PUBLIC_THEME,
  errors = {},
  onChange,
  disabled = false,
  registerFieldRef = () => {},
}) => {
  const { t, i18n } = useTranslation();
  const defaults = role === 'title'
    ? DEFAULT_SALON_TITLE_STYLE
    : DEFAULT_SALON_CONTENT_STYLE;
  const styleValue = { ...defaults, ...value };
  const roleLabel = t(`salon_theme.styles.${role}`);
  const direction = typeof i18n?.dir === 'function' && i18n.dir() === 'rtl'
    ? 'rtl'
    : 'ltr';
  const resolvedTheme = buildSalonPublicTheme(theme, direction);
  const effectiveFontKey = resolvedTheme.config.font_key;
  const inheritedColor = role === 'title'
    ? resolvedTheme.tokens.salon.primary
    : resolvedTheme.tokens.colors.text.primary;
  const accessibleLabel = (field) => (
    `${t(`salon_theme.styles.${field}`)} — ${roleLabel}`
  );
  const isBold = styleValue.weight >= 700;
  const alignmentOptions = [
    {
      value: 'start',
      Icon: direction === 'rtl' ? FormatAlignRightIcon : FormatAlignLeftIcon,
    },
    { value: 'center', Icon: FormatAlignCenterIcon },
    {
      value: 'end',
      Icon: direction === 'rtl' ? FormatAlignLeftIcon : FormatAlignRightIcon,
    },
  ];

  return (
    <Stack
      data-testid={`salon-text-style-controls-${sectionKey}-${role}`}
      spacing={1.25}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, textAlign: 'start' }}>
        {roleLabel}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr) 68px auto',
            sm: 'minmax(125px, 1.15fr) minmax(105px, 1fr) 68px auto',
          },
          gap: 1,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}>
          <SalonEffectiveColorControl
            label={accessibleLabel('color')}
            value={styleValue.color}
            inheritedColor={inheritedColor}
            error={firstError(errors?.color)}
            onChange={(nextValue) => onChange('color', nextValue)}
            resetLabel={t('salon_theme.styles.use_theme_color')}
            disabled={disabled}
            inputRef={(element) => registerFieldRef('color', element)}
            hideVisibleLabel
          />
        </Box>

        <FormControl
          size="small"
          error={Boolean(firstError(errors?.font_key))}
          disabled={disabled}
          sx={{ minWidth: 0 }}
        >
          <Select
            native
            value={styleValue.font_key}
            onChange={(event) => onChange('font_key', event.target.value)}
            inputRef={(element) => registerFieldRef('font_key', element)}
            inputProps={{ 'aria-label': accessibleLabel('font') }}
          >
            <option value="inherit">
              {SALON_FONT_CATALOG[effectiveFontKey]?.label}
            </option>
            {Object.entries(SALON_FONT_CATALOG)
              .filter(([key]) => (
                styleValue.font_key !== 'inherit' || key !== effectiveFontKey
              ))
              .map(([key, preset]) => (
                <option key={key} value={key}>{preset.label}</option>
              ))}
          </Select>
          {firstError(errors?.font_key) && (
            <FormHelperText role="alert">{firstError(errors.font_key)}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          size="small"
          error={Boolean(firstError(errors?.size))}
          disabled={disabled}
        >
          <Select
            native
            value={styleValue.size}
            onChange={(event) => onChange('size', event.target.value)}
            inputRef={(element) => registerFieldRef('size', element)}
            inputProps={{ 'aria-label': accessibleLabel('size') }}
          >
            {Object.entries(SIZE_LABELS).map(([option, label]) => (
              <option key={option} value={option}>{label}</option>
            ))}
          </Select>
          {firstError(errors?.size) && (
            <FormHelperText role="alert">{firstError(errors.size)}</FormHelperText>
          )}
        </FormControl>

        <ToggleButtonGroup size="small" disabled={disabled}>
          <ToggleButton
            value="bold"
            selected={isBold}
            aria-label={accessibleLabel('bold')}
            onClick={() => onChange('weight', isBold ? defaults.weight : 700)}
            ref={(element) => registerFieldRef('weight', element)}
            sx={{ minWidth: 40, fontWeight: 800 }}
          >
            {t('salon_theme.styles.bold_short')}
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={Boolean(styleValue.italic)}
            aria-label={accessibleLabel('italic')}
            onClick={() => onChange('italic', !styleValue.italic)}
            ref={(element) => registerFieldRef('italic', element)}
            sx={{ minWidth: 40, fontStyle: 'italic', fontFamily: 'serif' }}
          >
            {t('salon_theme.styles.italic_short')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={styleValue.alignment}
        onChange={(_event, nextAlignment) => {
          if (nextAlignment) onChange('alignment', nextAlignment);
        }}
        disabled={disabled}
        aria-label={accessibleLabel('alignment')}
        sx={{ alignSelf: 'flex-start' }}
      >
        {alignmentOptions.map(({ value: alignment, Icon }, index) => (
          <ToggleButton
            key={alignment}
            value={alignment}
            aria-label={`${t(`salon_theme.styles.options.alignment.${alignment}`)} — ${roleLabel}`}
            ref={index === 0
              ? (element) => registerFieldRef('alignment', element)
              : undefined}
            sx={{ minWidth: 48 }}
          >
            <Icon fontSize="small" />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {[firstError(errors?.weight), firstError(errors?.italic), firstError(errors?.alignment)]
        .filter(Boolean)
        .map((message) => (
          <FormHelperText key={message} error role="alert">{message}</FormHelperText>
        ))}
    </Stack>
  );
};

export default SalonTextStyleControls;
